import { safe } from "@orpc/client";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import z from "zod";
import { useAppForm } from "@/components/form";
import { ComboboxCategory } from "@/components/forms/combobox-category";
import { ComboboxLanguage } from "@/components/forms/combobox-language";
import { ComboboxTags } from "@/components/forms/combobox-tags";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	FieldTitle,
} from "@/components/ui/field";
import {
	FormCard,
	FormCardContent,
	FormCardHeader,
	FormCardTitle,
} from "@/components/ui/form-card";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupText,
} from "@/components/ui/input-group";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AdditionalDataType } from "@/enums/additional-data-type";
import { ChannelType } from "@/enums/channel-type";
import { orpc, validationErrors } from "@/orpc/client";

export const Route = createFileRoute("/_app/channels/$channelId/")({
	component: Page,
	params: z.object({
		channelId: z.coerce.number(),
	}),
	async loader({ context, params }) {
		context.queryClient.prefetchQuery(
			orpc.channels.show.queryOptions({
				input: {
					channelId: params.channelId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data: channel } = useSuspenseQuery(
		orpc.channels.show.queryOptions({
			input: {
				channelId: params.channelId,
			},
		}),
	);

	const updateMutation = useMutation(
		orpc.channels.updateDetails.mutationOptions({
			onSuccess: (_data, _payload, _result, context) => {
				context.client.invalidateQueries({ queryKey: orpc.channels.key() });
				toast.success("Channel updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update this channel.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			Author: channel.Author || "",
			CategoryID:
				channel.ChannelCategories.length > 0
					? channel.ChannelCategories[0].CategoryID
					: null,
			Copyright: channel.Copyright || "",
			Description: channel.Description || "",
			HomepageUrl: channel.HomepageUrl || "",
			IsExplicit: channel.IsExplicit || false,
			LanguageCode: channel.LanguageCode,
			OwnerEmail: channel.OwnerEmail || "",
			OwnerName: channel.OwnerName || "",
			Summary: channel.Summary || "",
			Tags:
				channel.AdditionalData?.find(
					(data) => data.AdditionalDataID === AdditionalDataType.ChannelTags, // Assuming 1 is the ID for tags
				)?.ChannelAdditionalDataValue.split(",") || [],
			Title: channel.Title || "",
			TypeID: channel.TypeID,
		},
		canSubmitWhenInvalid: true,
		validators: {
			onSubmitAsync: async ({ value }) => {
				const { error } = await safe(
					updateMutation.mutateAsync({
						...value,
						ID: channel.ID,
					}),
				);

				if (error) {
					return (
						validationErrors(error) ??
						"An error occurred while updating the channel."
					);
				}
			},
		},
	});

	return (
		<form.AppForm>
			<form
				className="relative"
				id={form.formId}
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<div className="px-4 lg:px-6 grid grid-cols-12 gap-6">
					<FormCard className="col-span-12" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Channel Info</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="Title"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Title</field.FormLabel>
											<InputGroup>
												<field.FormGroupInput />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/150 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Summary"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Summary</field.FormLabel>
											<InputGroup>
												<field.FormGroupTextarea rows={3} />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/4000 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Description"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Description</field.FormLabel>
											<InputGroup>
												<field.FormGroupTextarea rows={10} />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/4000 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="HomepageUrl"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Homepage URL</field.FormLabel>
											<field.FormInput type="url" />
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<FormCard className="col-span-12" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Channel Content</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="LanguageCode"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-channel-language`}
											className="col-span-6"
										>
											<field.FormLabel>Language</field.FormLabel>
											<ComboboxLanguage
												id={`${field.form.formId}-channel-language`}
												value={field.state.value}
												onChange={(value) => field.handleChange(value)}
												onBlur={field.handleBlur}
												isInvalid={
													field.state.meta.isTouched &&
													!field.state.meta.isValid
												}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="IsExplicit"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-channel-explicit`}
											className="col-span-6"
										>
											<field.FormLabel>Explicit Content</field.FormLabel>
											<RadioGroup className="flex flex-row gap-2">
												<FieldLabel>
													<Field
														orientation="horizontal"
														className="py-1.5! h-8"
													>
														<FieldContent>
															<FieldTitle>Yes</FieldTitle>
														</FieldContent>
														<RadioGroupItem value={true} />
													</Field>
												</FieldLabel>
												<FieldLabel>
													<Field
														orientation="horizontal"
														className="py-1.5! h-8"
													>
														<FieldContent>
															<FieldTitle>No</FieldTitle>
														</FieldContent>
														<RadioGroupItem value={false} />
													</Field>
												</FieldLabel>
											</RadioGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="CategoryID"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-channel-category`}
											className="col-span-6"
										>
											<field.FormLabel>Category</field.FormLabel>
											<ComboboxCategory
												id={`${field.form.formId}-channel-category`}
												value={field.state.value}
												onChange={(value) => field.handleChange(value)}
												onBlur={field.handleBlur}
												isInvalid={
													field.state.meta.isTouched &&
													!field.state.meta.isValid
												}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Tags"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-channel-tags`}
											className="col-span-6"
										>
											<field.FormLabel>Tags</field.FormLabel>
											<ComboboxTags
												id={`${field.form.formId}-channel-tags`}
												value={field.state.value}
												onChange={(value) => field.handleChange(value)}
												onBlur={field.handleBlur}
												isInvalid={
													field.state.meta.isTouched &&
													!field.state.meta.isValid
												}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<FormCard
						className="lg:col-span-6"
						disabled={updateMutation.isPending}
					>
						<FormCardHeader>
							<FormCardTitle>Channel Type</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup className="col-span-12">
								<form.AppField
									name="TypeID"
									children={(field) => (
										<RadioGroup
											value={field.state.value}
											onValueChange={field.handleChange}
											onBlur={field.handleBlur}
											className="col-span-full"
										>
											<FieldLabel htmlFor="episodic-type">
												<Field orientation="horizontal">
													<FieldContent>
														<FieldTitle>Episodic</FieldTitle>
														<FieldDescription className="text-xs">
															This is the standard format displaying episodes
															from most recent to oldest. This setting is ideal
															for podcasts where each episode can be enjoyed
															independently.
														</FieldDescription>
													</FieldContent>
													<RadioGroupItem
														value={ChannelType.Episodic}
														id="episodic-type"
													/>
												</Field>
											</FieldLabel>
											<FieldLabel htmlFor="serial-type">
												<Field orientation="horizontal">
													<FieldContent>
														<FieldTitle>Serial</FieldTitle>
														<FieldDescription className="text-xs">
															For shows where episodes follow a chronological
															story and are best experienced in order from the
															beginning, organized by seasons. This setting
															works well for narrative-driven and storytelling
															podcasts.
														</FieldDescription>
													</FieldContent>
													<RadioGroupItem
														value={ChannelType.Serial}
														id="serial-type"
													/>
												</Field>
											</FieldLabel>
										</RadioGroup>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<FormCard
						className="lg:col-span-6"
						disabled={updateMutation.isPending}
					>
						<FormCardHeader>
							<FormCardTitle>Channel Author</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="Author"
									children={(field) => (
										<field.FormItem className="xl:col-span-6">
											<field.FormLabel>Author</field.FormLabel>
											<field.FormInput maxLength={200} />
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Copyright"
									children={(field) => (
										<field.FormItem className="xl:col-span-6">
											<field.FormLabel>Copyright</field.FormLabel>
											<field.FormInput maxLength={200} />
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="OwnerName"
									children={(field) => (
										<field.FormItem className="xl:col-span-6">
											<field.FormLabel>Owner Name</field.FormLabel>
											<field.FormInput maxLength={200} />
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="OwnerEmail"
									children={(field) => (
										<field.FormItem className="xl:col-span-6">
											<field.FormLabel>Owner Email</field.FormLabel>
											<field.FormInput maxLength={200} />
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<form.FormSaveBar />
				</div>
				<form.FormBottomBar />
			</form>
		</form.AppForm>
	);
}
